import { formatPace, parsePace, ZONE_LABELS, zoneBounds } from '@painpal/core';
import {
  thresholdRangeError, type SportSuggestion, type Threshold, type ThresholdInput, type ThresholdKind, type ThresholdSport,
} from '@painpal/contracts';
import type { SportSuggestionAction } from '../../api/paths';
import { formatDayMonth, formatDayMonthYear, formatPaceKm } from '../../lib/format';
import { plural } from '../../lib/plural';
import { SPORT_META } from '../../lib/sports';
import { boundRange } from '../../lib/zones';

export const MAX_STEP_PX = 100;
export const DEFAULT_FTP = 200;

export interface ThresholdCopy {
  sport: ThresholdSport;
  kind: ThresholdKind;
  short: string;
  unit: 'W' | 'BPM' | '/KM';
  fallback: number;
  emptyBody: string;
  covered: readonly [string, string];
}

const RIDES = ['RIDE', 'RIDES'] as const;

export const THRESHOLD_COPY: Record<ThresholdKind, ThresholdCopy> = {
  ftp: { sport: 'ride', kind: 'ftp', short: 'FTP', unit: 'W', fallback: DEFAULT_FTP, emptyBody: 'Add your FTP so rides with power get a Load.', covered: RIDES },
  lthr: { sport: 'ride', kind: 'lthr', short: 'LTHR', unit: 'BPM', fallback: 165, emptyBody: 'Add your lactate threshold heart rate to see 7 HR zones on every ride.', covered: RIDES },
  max_hr: { sport: 'ride', kind: 'max_hr', short: 'MAX HR', unit: 'BPM', fallback: 185, emptyBody: 'Add your max heart rate to see 5 HR zones on every ride.', covered: RIDES },
  threshold_pace: {
    sport: 'run', kind: 'threshold_pace', short: 'THRESHOLD PACE', unit: '/KM', fallback: 300,
    emptyBody: 'Add your threshold pace so runs with GPS or a pace stream get a Load.', covered: ['RUN', 'RUNS'],
  },
};

export const SPORT_LTHR_COPY: Record<'run' | 'other', ThresholdCopy> = {
  run: { sport: 'run', kind: 'lthr', short: 'THRESHOLD HR', unit: 'BPM', fallback: 170, emptyBody: 'Add your run threshold HR so runs without pace get a Load.', covered: ['RUN', 'RUNS'] },
  other: {
    sport: 'other', kind: 'lthr', short: 'THRESHOLD HR', unit: 'BPM', fallback: 160,
    emptyBody: 'Add a threshold HR so swims, walks, strength and other sessions with HR get a Load.', covered: ['ACTIVITY', 'ACTIVITIES'],
  },
};

export const formatThresholdValue = (copy: ThresholdCopy, value: number): string =>
  (copy.unit === '/KM' ? formatPaceKm(value) : `${value} ${copy.unit}`);

export const coveredCount = (copy: ThresholdCopy, n: number): string => `${n} ${plural(n, copy.covered[0], copy.covered[1])}`;

// Other sports group several sports, so their history says which ones each entry covered.
export function coveredLabel(copy: ThresholdCopy, t: Pick<Threshold, 'ridesCovered' | 'sportsCovered'>): string {
  if (copy.sport !== 'other') return coveredCount(copy, t.ridesCovered);
  const sports = t.sportsCovered.map((s) => SPORT_META[s].label);
  return sports.length === 0 ? coveredCount(copy, t.ridesCovered) : `${t.ridesCovered} · ${sports.join(', ')}`;
}

export function sortAscending(items: Threshold[]): Threshold[] {
  return [...items].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
}

export function currentThreshold(items: Threshold[], today: string): Threshold | undefined {
  return sortAscending(items).filter((t) => t.effectiveFrom <= today).at(-1);
}

export interface StepSegment { id: string; label: string; effectiveFrom: string; heightPx: number; current: boolean }

export function thresholdSegments(items: Threshold[], today: string, copy: ThresholdCopy): StepSegment[] {
  const sorted = sortAscending(items);
  // A faster pace is a smaller number, so pace bars scale by speed: the fastest entry is the tallest.
  const size = (v: number): number => (copy.unit === '/KM' ? 1 / v : v);
  const max = Math.max(...sorted.map((t) => size(t.value)));
  const current = currentThreshold(items, today)?.id;
  return sorted.map((t) => ({
    id: t.id, label: formatThresholdValue(copy, t.value), effectiveFrom: t.effectiveFrom,
    heightPx: Math.round((size(t.value) / max) * MAX_STEP_PX), current: t.id === current,
  }));
}

export function stepChartLabel(segments: StepSegment[], copy: ThresholdCopy): string {
  return `${copy.short} history: ${segments.map((s) => `${s.label} from ${formatDayMonthYear(s.effectiveFrom)}`).join(', ')}`;
}

// Only ride HR thresholds leave Load alone; run and other-sports HR, like FTP and pace, change Load.
export type RecalcKind = 'ftp' | 'hr';

export const recalcKindOf = (sport: ThresholdSport, kind: ThresholdKind): RecalcKind => (sport === 'ride' && kind !== 'ftp' ? 'hr' : 'ftp');

const activityCount = (n: number): string => `${n} ${plural(n, 'ACTIVITY', 'ACTIVITIES')}`;

export function recalcMessage(recalculating: { rides: number; fromDate: string }, kind: RecalcKind): string {
  const count = activityCount(recalculating.rides);
  // HR changes reach back to old activities, so the date carries its year.
  if (kind === 'hr') return `RECALCULATING ${count} FROM ${formatDayMonthYear(recalculating.fromDate)} · HR ZONES UPDATE WHEN THIS FINISHES`;
  return `RECALCULATING ${count} FROM ${formatDayMonth(recalculating.fromDate)} · YOUR FITNESS CHART UPDATES WHEN THIS FINISHES`;
}

// Entries Strava or a ride supplied say so; ones the athlete typed stay unlabelled.
export function sourceLabel(threshold: Pick<Threshold, 'source' | 'sourceName'>): string | null {
  if (threshold.source === 'manual') return null;
  return (threshold.source === 'strava' ? 'Strava' : (threshold.sourceName ?? 'a ride')).toUpperCase();
}

export function thresholdRowMeta(copy: ThresholdCopy, threshold: Pick<Threshold, 'effectiveFrom' | 'ridesCovered' | 'sportsCovered' | 'source' | 'sourceName'>): string {
  const from = `FROM ${formatDayMonthYear(threshold.effectiveFrom)}`;
  const source = sourceLabel(threshold);
  return source === null ? `${from} · ${coveredLabel(copy, threshold)}` : `${from} · VIA ${source}`;
}

export const acceptStepsLabel = (steps: number): string => `ACCEPT ${steps} ${plural(steps, 'STEP')}`;

export function toggled(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

// Phone number pads have no colon key, so the pace field takes digits only and writes the colon itself: 450 → 4:50.
export function maskPace(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 4);
  return digits.length < 3 ? digits : `${digits.slice(0, -2)}:${digits.slice(-2)}`;
}

// The input re-masks every change, so the caret is kept by how many digits sit before it, and deleting the colon alone
// (which masking would only put back) deletes the digit before it instead. Only a pure one-character delete counts:
// typing over a selection that held the colon replaces digits too.
export function editPace(previous: string, next: string, caret: number): { value: string; caret: number } {
  const colonDeleted = previous.includes(':') && !next.includes(':') && next === previous.slice(0, caret) + previous.slice(caret + 1);
  const before = next.slice(0, caret).replace(/\D/g, '');
  const after = next.slice(caret).replace(/\D/g, '');
  const kept = colonDeleted ? before.slice(0, -1) : before;
  const value = maskPace(kept + after);
  let position = 0;
  for (let seen = 0; seen < kept.length && position < value.length; position += 1) if (/\d/.test(value.charAt(position))) seen += 1;
  return { value, caret: position };
}

function parseValue(copy: ThresholdCopy, value: string): number | string {
  if (copy.unit === '/KM') return parsePace(value) ?? 'Enter a pace like 4:50';
  const n = Number(value);
  return Number.isInteger(n) ? n : `${copy.short} must be a whole number`;
}

export function parseThresholdInput(copy: ThresholdCopy, value: string, effectiveFrom: string): ThresholdInput | string {
  const n = parseValue(copy, value);
  if (typeof n === 'string') return n;
  const rangeError = thresholdRangeError(copy.kind, n);
  if (rangeError) return rangeError;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) return 'Choose a valid-from date';
  return { value: n, effectiveFrom };
}

// Pace bounds are descending s/km from Z2 up; each middle zone is labelled by its fastest edge, as the board's strip is.
export function paceStripLabels(bounds: readonly number[]): string[] {
  const middle = bounds.slice(1).map((b, i) => `Z${i + 2} ${formatPace(b + 1)}`);
  return [`Z1 > ${formatPace(Math.max(...bounds))}`, ...middle, `Z5 ≤ ${formatPace(Math.min(...bounds))}`];
}

export function hrStripLabels(bounds: readonly number[]): string[] {
  return ZONE_LABELS.hr_lthr.map((label, i) => `${label} ${boundRange(bounds, i, '').trim()}`);
}

export const paceZoneBounds = (thresholdPaceS: number): number[] => zoneBounds('pace', thresholdPaceS);
export const hrZoneBounds = (thresholdHr: number): number[] => zoneBounds('hr_lthr', thresholdHr);

export interface SuggestionCopy { label: string; value: string; yours: string | null; why: string | null }
type Suggested = Extract<SportSuggestion, { state: 'suggest' }>;

const basisLine = (s: Suggested): string => (s.basis === null ? '' : ` · ${s.basis.name.toUpperCase()}, ${formatDayMonth(s.basis.localDate)}`);

export function suggestionCopy(action: SportSuggestionAction, s: Suggested): SuggestionCopy {
  const copy = action === 'run-pace' ? THRESHOLD_COPY.threshold_pace : SPORT_LTHR_COPY[action === 'run-lthr' ? 'run' : 'other'];
  const yours = s.yours === null ? null : `· YOURS ${formatThresholdValue(copy, s.yours)}`;
  const value = formatThresholdValue(copy, s.value);
  if (action === 'run-pace') return { label: 'SUGGESTED FROM YOUR RUNS', value, yours, why: `BEST 30-MIN GRADE-ADJUSTED PACE IN YOUR LAST 6 WEEKS OF RUNS${basisLine(s)}` };
  if (action === 'run-lthr') return { label: 'SUGGESTED FROM YOUR RUNS', value, yours, why: `95% OF YOUR BEST 20-MIN HR${basisLine(s)}` };
  return { label: `USE YOUR ${s.from === 'ride' ? 'RIDE' : 'RUN'} THRESHOLD HR`, value, yours, why: null };
}
